import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputAdornment,
  Chip,
  Tabs,
  Tab,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SaveIcon from '@mui/icons-material/Save';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SettingsIcon from '@mui/icons-material/Settings';
import { useTranslation } from 'react-i18next';
import { getB2BSettings, saveB2BSettings, B2BSettingsData } from '../../../services/ecommerceApi';
import { accountingApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';
import { AppDatePicker } from '../../common/AppDatePicker';
import GuideButton from '../../common/guide/GuideButton';

interface B2BManagementProps {
  companyId: string | null;
}

interface BankAccountOption {
  id: number;
  bankName?: string;
  accountName?: string;
  currency?: string;
}

interface CurrencyOption {
  id: number;
  code?: string;
  name?: string;
}

const DEFAULT_CURRENCIES = ['TRY', 'USD', 'EUR', 'GBP'];

const B2BManagement: React.FC<B2BManagementProps> = ({ companyId }) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (tr: string, en: string) => (isTr ? tr : en);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<B2BSettingsData>({
    storeMode: 'b2c_only',
    hidePricesWhenLoggedOut: false,
    pricesVisibleToB2BOnly: false,
    allowB2CRegistration: true,
    allowB2CLogin: true,
    allowAquasoftLogin: true,
    allowBranchLogin: true,
    b2bPricesVatExcluded: false,
    storeCurrency: 'TRY',
    exchangeRateMode: 'manual',
    manualExchangeRate: null,
    showExchangeRateInHeader: false,
    b2cPricesInTry: false,
    onlineCollectionBankAccountId: null,
    storeUrl: null
  });
  const [initialStoreMode, setInitialStoreMode] = useState<string>('b2c_only');
  const [bankAccounts, setBankAccounts] = useState<BankAccountOption[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyOption[]>([]);
  const [modeWarningOpen, setModeWarningOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });

  // ===== Faz 5.4: Cari Ekstre görünümü =====
  const [activeView, setActiveView] = useState(0);
  const [cariAccounts, setCariAccounts] = useState<any[]>([]);
  const [cariAccountsLoading, setCariAccountsLoading] = useState(false);
  const [statementAccount, setStatementAccount] = useState<any | null>(null);
  const [statementStartDate, setStatementStartDate] = useState('');
  const [statementEndDate, setStatementEndDate] = useState('');
  const [statementRows, setStatementRows] = useState<any[]>([]);
  const [statementLoading, setStatementLoading] = useState(false);
  const [statementPage, setStatementPage] = useState(0);
  const [statementRowsPerPage, setStatementRowsPerPage] = useState(25);

  const loadSeqRef = useRef(0);
  const dirtyRef = useRef(false);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  const applySettingsFromApi = (d: B2BSettingsData) => {
    setSettings({
      configId: d.configId,
      storeMode: d.storeMode || 'b2c_only',
      hidePricesWhenLoggedOut: d.hidePricesWhenLoggedOut === true,
      pricesVisibleToB2BOnly: d.pricesVisibleToB2BOnly === true,
      allowB2CRegistration: d.allowB2CRegistration !== false,
      allowB2CLogin: d.allowB2CLogin !== false,
      allowAquasoftLogin: d.allowAquasoftLogin !== false,
      allowBranchLogin: d.allowBranchLogin !== false,
      b2bPricesVatExcluded: d.b2bPricesVatExcluded === true,
      storeCurrency: d.storeCurrency || 'TRY',
      exchangeRateMode: d.exchangeRateMode || 'manual',
      manualExchangeRate: d.manualExchangeRate ?? null,
      showExchangeRateInHeader: d.showExchangeRateInHeader === true,
      b2cPricesInTry: d.b2cPricesInTry === true,
      onlineCollectionBankAccountId: d.onlineCollectionBankAccountId ?? null,
      storeUrl: d.storeUrl ?? null
    });
    setInitialStoreMode(d.storeMode || 'b2c_only');
  };

  const loadData = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    try {
      const [settingsRes, bankRes, currencyRes] = await Promise.allSettled([
        getB2BSettings(companyId || undefined),
        accountingApi.get(ACCOUNTING_API.BANK_ACCOUNT_BY_COMPANY),
        accountingApi.get(ACCOUNTING_API.CURRENCY_LIST)
      ]);

      if (seq !== loadSeqRef.current) return;

      // Kullanıcı switch'e dokunduysa GET cevabı geri açmasın
      if (!dirtyRef.current && settingsRes.status === 'fulfilled' && settingsRes.value?.data) {
        applySettingsFromApi(settingsRes.value.data);
      }

      if (bankRes.status === 'fulfilled') {
        const raw = bankRes.value.data?.data || bankRes.value.data || [];
        setBankAccounts(Array.isArray(raw) ? raw : []);
      }

      if (currencyRes.status === 'fulfilled') {
        const raw = currencyRes.value.data?.data || currencyRes.value.data || [];
        setCurrencies(Array.isArray(raw) ? raw : []);
      }
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      console.error('[B2BManagement] Veriler yüklenemedi:', err);
      setSnackbar({
        open: true,
        message: tt('B2B ayarları yüklenirken hata oluştu', 'Failed to load B2B settings'),
        severity: 'error'
      });
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    dirtyRef.current = false;
    loadData();
  }, [loadData]);

  const persistSettings = async (
    override: Partial<B2BSettingsData> = {},
    options?: { skipExchangeValidation?: boolean }
  ) => {
    const current = { ...settingsRef.current, ...override };

    if (
      !options?.skipExchangeValidation &&
      current.exchangeRateMode === 'manual' &&
      current.storeCurrency !== 'TRY' &&
      (!current.manualExchangeRate || current.manualExchangeRate <= 0)
    ) {
      setSnackbar({
        open: true,
        message: tt('Manuel kur modunda geçerli bir kur girmelisiniz.', 'A valid exchange rate is required in manual mode.'),
        severity: 'error'
      });
      return false;
    }

    setSaving(true);
    try {
      const payload: B2BSettingsData = {
        storeMode: current.storeMode,
        hidePricesWhenLoggedOut: current.hidePricesWhenLoggedOut === true,
        pricesVisibleToB2BOnly: current.pricesVisibleToB2BOnly === true,
        allowB2CRegistration: current.allowB2CRegistration !== false,
        allowB2CLogin: override.allowB2CLogin !== undefined
          ? override.allowB2CLogin === true
          : current.allowB2CLogin !== false,
        allowAquasoftLogin: override.allowAquasoftLogin !== undefined
          ? override.allowAquasoftLogin === true
          : current.allowAquasoftLogin !== false,
        allowBranchLogin: override.allowBranchLogin !== undefined
          ? override.allowBranchLogin === true
          : current.allowBranchLogin !== false,
        b2bPricesVatExcluded: current.b2bPricesVatExcluded === true,
        storeCurrency: current.storeCurrency,
        exchangeRateMode: current.exchangeRateMode,
        manualExchangeRate: current.manualExchangeRate ?? null,
        showExchangeRateInHeader: current.showExchangeRateInHeader === true,
        b2cPricesInTry: current.b2cPricesInTry === true,
        onlineCollectionBankAccountId: current.onlineCollectionBankAccountId ?? null,
        storeUrl: current.storeUrl?.trim() ? current.storeUrl.trim() : null
      };

      console.log('[B2BManagement] Saving payload:', payload);
      const res = await saveB2BSettings(payload, companyId || undefined);
      if (!res?.success) throw new Error(res?.message || 'save failed');

      const saved = res.data;
      if (saved && saved.storeMode != null) {
        // Tam ayar objesi döndü
        applySettingsFromApi(saved);
      } else if (saved) {
        // Eski API yalnızca configId döndürebilir — mevcut UI state'i koru
        setSettings(prev => ({
          ...prev,
          ...override,
          configId: saved.configId ?? prev.configId,
          hidePricesWhenLoggedOut: override.hidePricesWhenLoggedOut !== undefined
            ? override.hidePricesWhenLoggedOut === true
            : prev.hidePricesWhenLoggedOut === true,
          pricesVisibleToB2BOnly: override.pricesVisibleToB2BOnly !== undefined
            ? override.pricesVisibleToB2BOnly === true
            : prev.pricesVisibleToB2BOnly === true,
          allowB2CLogin: override.allowB2CLogin !== undefined
            ? override.allowB2CLogin !== false
            : prev.allowB2CLogin !== false,
          allowAquasoftLogin: override.allowAquasoftLogin !== undefined
            ? override.allowAquasoftLogin !== false
            : prev.allowAquasoftLogin !== false,
          allowBranchLogin: override.allowBranchLogin !== undefined
            ? override.allowBranchLogin !== false
            : prev.allowBranchLogin !== false
        }));
        setInitialStoreMode(current.storeMode || 'b2c_only');
      } else {
        setSettings(prev => ({ ...prev, ...override }));
        setInitialStoreMode(current.storeMode || 'b2c_only');
      }
      dirtyRef.current = false;
      setSnackbar({
        open: true,
        message: tt('B2B ayarları kaydedildi.', 'B2B settings saved.'),
        severity: 'success'
      });
      return true;
    } catch (err: any) {
      console.error('[B2BManagement] Kaydetme hatası:', err);
      const apiMessage = err?.response?.data?.message;
      setSnackbar({
        open: true,
        message: apiMessage || tt('B2B ayarları kaydedilemedi.', 'Failed to save B2B settings.'),
        severity: 'error'
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleChange = <K extends keyof B2BSettingsData>(field: K, value: B2BSettingsData[K]) => {
    dirtyRef.current = true;
    loadSeqRef.current += 1;
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  /** Mağaza modu da switch'ler gibi anında persist edilir — aksi halde GET/yenile Hibrit'e geri çeker. */
  const handleStoreModeChange = async (value: string) => {
    const previous = settingsRef.current.storeMode || 'b2c_only';
    if (value === previous) return;
    dirtyRef.current = true;
    loadSeqRef.current += 1;
    setSettings(prev => ({ ...prev, storeMode: value }));
    const ok = await persistSettings(
      { storeMode: value },
      { skipExchangeValidation: true }
    );
    if (!ok) {
      setSettings(prev => ({ ...prev, storeMode: previous }));
    }
  };

  /** Bu switch kaydı beklemeden anında persist edilir — GET yarışı geri açmasın */
  const handleLoginRequiredToggle = async (checked: boolean) => {
    dirtyRef.current = true;
    loadSeqRef.current += 1;
    setSettings(prev => ({ ...prev, hidePricesWhenLoggedOut: checked }));
    const ok = await persistSettings(
      { hidePricesWhenLoggedOut: checked },
      { skipExchangeValidation: true }
    );
    if (!ok) {
      // Kayıt başarısızsa UI'ı geri al
      setSettings(prev => ({ ...prev, hidePricesWhenLoggedOut: !checked }));
    }
  };

  const handleB2BOnlyPricesToggle = async (checked: boolean) => {
    dirtyRef.current = true;
    loadSeqRef.current += 1;
    setSettings(prev => ({ ...prev, pricesVisibleToB2BOnly: checked }));
    const ok = await persistSettings(
      { pricesVisibleToB2BOnly: checked },
      { skipExchangeValidation: true }
    );
    if (!ok) {
      setSettings(prev => ({ ...prev, pricesVisibleToB2BOnly: !checked }));
    }
  };

  const handleB2CLoginToggle = async (checked: boolean) => {
    dirtyRef.current = true;
    loadSeqRef.current += 1;
    setSettings(prev => ({ ...prev, allowB2CLogin: checked }));
    const ok = await persistSettings(
      { allowB2CLogin: checked },
      { skipExchangeValidation: true }
    );
    if (!ok) {
      setSettings(prev => ({ ...prev, allowB2CLogin: !checked }));
    }
  };

  const handleAquasoftLoginToggle = async (checked: boolean) => {
    dirtyRef.current = true;
    loadSeqRef.current += 1;
    setSettings(prev => ({ ...prev, allowAquasoftLogin: checked }));
    const ok = await persistSettings(
      { allowAquasoftLogin: checked },
      { skipExchangeValidation: true }
    );
    if (!ok) {
      setSettings(prev => ({ ...prev, allowAquasoftLogin: !checked }));
    }
  };

  const handleBranchLoginToggle = async (checked: boolean) => {
    dirtyRef.current = true;
    loadSeqRef.current += 1;
    setSettings(prev => ({ ...prev, allowBranchLogin: checked }));
    const ok = await persistSettings(
      { allowBranchLogin: checked },
      { skipExchangeValidation: true }
    );
    if (!ok) {
      setSettings(prev => ({ ...prev, allowBranchLogin: !checked }));
    }
  };

  // Faz 5.6: hybrid → b2b_only geçişinde uyarı
  const handleSaveClick = () => {
    if (settings.storeMode === 'b2b_only' && initialStoreMode !== 'b2b_only') {
      setModeWarningOpen(true);
      return;
    }
    void persistSettings();
  };

  const doSave = async () => {
    setModeWarningOpen(false);
    await persistSettings();
  };

  // ===== Faz 5.4: Cari ekstre işlemleri =====
  const loadCariAccounts = useCallback(async () => {
    if (cariAccounts.length > 0) return;
    setCariAccountsLoading(true);
    try {
      const res = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_COMPANY);
      const list = res.data?.data || res.data || [];
      setCariAccounts(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('[B2BManagement] Cari listesi yüklenemedi:', err);
      setSnackbar({
        open: true,
        message: tt('Cari listesi yüklenemedi.', 'Failed to load accounts.'),
        severity: 'error'
      });
    } finally {
      setCariAccountsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cariAccounts.length]);

  useEffect(() => {
    if (activeView === 1) loadCariAccounts();
  }, [activeView, loadCariAccounts]);

  const loadStatement = async () => {
    if (!statementAccount?.id) return;
    setStatementLoading(true);
    setStatementPage(0);
    try {
      const params = new URLSearchParams();
      if (statementStartDate) params.append('startDate', new Date(statementStartDate).toISOString());
      if (statementEndDate) {
        const end = new Date(statementEndDate);
        end.setHours(23, 59, 59, 999);
        params.append('endDate', end.toISOString());
      }
      const qs = params.toString();
      const res = await accountingApi.get(
        `${ACCOUNTING_API.ACCOUNT_STATEMENT(statementAccount.id)}${qs ? `?${qs}` : ''}`
      );
      const rows = res.data?.data || [];
      // En yeni hareket üstte gösterilir
      setStatementRows(Array.isArray(rows) ? [...rows].reverse() : []);
    } catch (err) {
      console.error('[B2BManagement] Ekstre yüklenemedi:', err);
      setSnackbar({
        open: true,
        message: tt('Cari ekstresi yüklenemedi.', 'Failed to load account statement.'),
        severity: 'error'
      });
      setStatementRows([]);
    } finally {
      setStatementLoading(false);
    }
  };

  const formatAmount = (value: any) => {
    const num = typeof value === 'number' ? value : parseFloat(value || '0');
    return new Intl.NumberFormat(isTr ? 'tr-TR' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num || 0);
  };

  const formatStatementDate = (row: any) => {
    const raw = row.date || row.createdAt;
    if (!raw) return '-';
    return new Date(raw).toLocaleDateString(isTr ? 'tr-TR' : 'en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const currencyOptions = React.useMemo(() => {
    const codes = new Set<string>(DEFAULT_CURRENCIES);
    currencies.forEach(c => { if (c.code) codes.add(c.code.toUpperCase()); });
    if (settings.storeCurrency) codes.add(settings.storeCurrency.toUpperCase());
    return Array.from(codes);
  }, [currencies, settings.storeCurrency]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isB2BOnly = settings.storeMode === 'b2b_only';
  const isB2COnly = !settings.storeMode || settings.storeMode === 'b2c_only';
  const isHybrid = settings.storeMode === 'hybrid';

  return (
    <Box>
      {/* Görünüm sekmeleri: Ayarlar / Cari Ekstre */}
      <Paper sx={{ mb: 3, display: 'flex', alignItems: 'center', pr: 1 }}>
        <Tabs
          value={activeView}
          onChange={(_, v) => setActiveView(v)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab icon={<SettingsIcon />} iconPosition="start" label={tt('B2B Ayarları', 'B2B Settings')} />
          <Tab icon={<ReceiptLongIcon />} iconPosition="start" label={tt('Cari Ekstre', 'Account Statement')} />
        </Tabs>
        <GuideButton guideKey="ecommerce.b2b" size="small" sx={{ ml: 'auto' }} />
      </Paper>

      {activeView === 1 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ReceiptLongIcon color="primary" />
            <Typography variant="h6">{tt('Cari Ekstre', 'Account Statement')}</Typography>
          </Box>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={cariAccounts}
                loading={cariAccountsLoading}
                getOptionLabel={(option: any) =>
                  `${option.companyName || ''}${option.accountCode ? ` (${option.accountCode})` : ''}`}
                isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                value={statementAccount}
                onChange={(_, value) => setStatementAccount(value)}
                renderInput={(params) => (
                  <TextField {...params} label={tt('Cari Hesap', 'Current Account')} placeholder={tt('Cari ara/seç...', 'Search/select account...')} />
                )}
              />
            </Grid>
            <Grid item xs={6} md={2.5}>
              <AppDatePicker
                fullWidth
                size="small"
                label={tt('Başlangıç', 'Start Date')}
                value={statementStartDate}
                onChange={setStatementStartDate}
              />
            </Grid>
            <Grid item xs={6} md={2.5}>
              <AppDatePicker
                fullWidth
                size="small"
                label={tt('Bitiş', 'End Date')}
                value={statementEndDate}
                onChange={setStatementEndDate}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                onClick={loadStatement}
                disabled={!statementAccount || statementLoading}
                startIcon={statementLoading ? <CircularProgress size={18} color="inherit" /> : <ReceiptLongIcon />}
              >
                {tt('Ekstreyi Getir', 'Load Statement')}
              </Button>
            </Grid>
            {statementAccount && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={`${tt('Güncel Bakiye', 'Current Balance')}: ${formatAmount(statementAccount.balance)} ${statementAccount.riskLimitCurrency || 'TRY'}`}
                    color={(statementAccount.balance ?? 0) >= 0 ? 'success' : 'error'}
                    variant="outlined"
                  />
                  {statementAccount.riskLimitAmount != null && (
                    <Chip
                      label={`${tt('Risk Limiti', 'Risk Limit')}: ${formatAmount(statementAccount.riskLimitAmount)}`}
                      variant="outlined"
                    />
                  )}
                  {statementAccount.isB2BDealer && (
                    <Chip label={tt('Bayi (B2B)', 'Dealer (B2B)')} color="primary" variant="outlined" />
                  )}
                </Box>
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 2 }} />

          {statementLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : statementRows.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              {statementAccount
                ? tt('Kayıt bulunamadı. Tarih aralığını değiştirip tekrar deneyin.', 'No records found. Adjust the date range and try again.')
                : tt('Ekstre için bir cari seçin.', 'Select an account to view the statement.')}
            </Typography>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{tt('Tarih', 'Date')}</TableCell>
                      <TableCell>{tt('Açıklama', 'Description')}</TableCell>
                      <TableCell>{tt('Belge No', 'Document No')}</TableCell>
                      <TableCell>{tt('Tip', 'Type')}</TableCell>
                      <TableCell align="right">{tt('Borç', 'Debit')}</TableCell>
                      <TableCell align="right">{tt('Alacak', 'Credit')}</TableCell>
                      <TableCell align="right">{tt('Bakiye', 'Balance')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statementRows
                      .slice(statementPage * statementRowsPerPage, statementPage * statementRowsPerPage + statementRowsPerPage)
                      .map((row: any) => (
                        <TableRow key={row.id} hover>
                          <TableCell>{formatStatementDate(row)}</TableCell>
                          <TableCell>{row.description || '-'}</TableCell>
                          <TableCell>{row.documentNo || '-'}</TableCell>
                          <TableCell>
                            <Chip size="small" variant="outlined" label={row.transactionType || '-'} />
                          </TableCell>
                          <TableCell align="right" sx={{ color: (row.debitAmount || 0) > 0 ? 'error.main' : 'inherit' }}>
                            {(row.debitAmount || 0) > 0 ? formatAmount(row.debitAmount) : '-'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: (row.creditAmount || 0) > 0 ? 'success.main' : 'inherit' }}>
                            {(row.creditAmount || 0) > 0 ? formatAmount(row.creditAmount) : '-'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatAmount(row.nextBalance)}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={statementRows.length}
                rowsPerPage={statementRowsPerPage}
                page={statementPage}
                onPageChange={(_, p) => setStatementPage(p)}
                onRowsPerPageChange={(e) => {
                  setStatementRowsPerPage(parseInt(e.target.value, 10));
                  setStatementPage(0);
                }}
                labelRowsPerPage={tt('Sayfa başına:', 'Rows per page:')}
              />
            </>
          )}
        </Paper>
      )}

      {activeView === 0 && (
      <>
      {/* Mağaza Modu */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <StorefrontIcon color="primary" />
          <Typography variant="h6">{tt('Mağaza Modu', 'Store Mode')}</Typography>
          <Chip
            size="small"
            label={
              isB2COnly
                ? tt('Sadece B2C (B2B kapalı)', 'B2C Only (B2B off)')
                : isB2BOnly
                  ? tt('Sadece B2B', 'B2B Only')
                  : tt('Hibrit (B2C + B2B)', 'Hybrid (B2C + B2B)')
            }
            color={isB2COnly ? 'default' : isB2BOnly ? 'warning' : 'success'}
            sx={{ ml: 1 }}
          />
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="store-mode-label">{tt('Mağaza Modu', 'Store Mode')}</InputLabel>
              <Select
                labelId="store-mode-label"
                value={settings.storeMode || 'b2c_only'}
                label={tt('Mağaza Modu', 'Store Mode')}
                disabled={saving}
                onChange={(e) => void handleStoreModeChange(String(e.target.value))}
              >
                <MenuItem value="b2c_only">{tt('Sadece B2C — B2B kapalı (varsayılan)', 'B2C only — B2B disabled (default)')}</MenuItem>
                <MenuItem value="hybrid">{tt('Hibrit — B2C + B2B birlikte', 'Hybrid — B2C + B2B together')}</MenuItem>
                <MenuItem value="b2b_only">{tt('Sadece B2B — yalnız onaylı bayiler', 'B2B only — approved dealers only')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!settings.hidePricesWhenLoggedOut}
                    disabled={saving}
                    onChange={(e) => void handleLoginRequiredToggle(e.target.checked)}
                  />
                }
                label={tt(
                  'Giriş zorunlu — ziyaretçiyi login sayfasına yönlendir',
                  'Require login — redirect visitors to the login page'
                )}
              />
              {!isB2BOnly && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!settings.pricesVisibleToB2BOnly}
                      disabled={saving}
                      onChange={(e) => void handleB2BOnlyPricesToggle(e.target.checked)}
                    />
                  }
                  label={tt(
                    'Fiyatları sadece onaylı B2B (bayi) üyeler görebilsin',
                    'Show prices only to approved B2B (dealer) members'
                  )}
                />
              )}
              {isHybrid && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.allowB2CLogin !== false}
                      disabled={saving}
                      onChange={(e) => void handleB2CLoginToggle(e.target.checked)}
                    />
                  }
                  label={tt(
                    'B2C kullanıcı girişi — bireysel üye giriş/kayıt sekmesi',
                    'B2C user login — individual member login/register tab'
                  )}
                />
              )}
              {(!isHybrid || settings.allowB2CLogin !== false) && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.allowB2CRegistration !== false}
                      onChange={(e) => handleChange('allowB2CRegistration', e.target.checked)}
                    />
                  }
                  label={tt('Son kullanıcı (B2C) kaydına izin ver', 'Allow B2C (end-user) registration')}
                />
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={!!settings.b2bPricesVatExcluded}
                    onChange={(e) => handleChange('b2bPricesVatExcluded', e.target.checked)}
                  />
                }
                label={tt('B2B/bayi kullanıcıya fiyatları KDV hariç göster', 'Show VAT-excluded prices to B2B/dealer users')}
              />
              {!isB2COnly && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.allowAquasoftLogin !== false}
                      disabled={saving}
                      onChange={(e) => void handleAquasoftLoginToggle(e.target.checked)}
                    />
                  }
                  label={tt(
                    'Aquasoft ile giriş — bayi, kendi Aquasoft e-posta/şifresiyle girebilsin',
                    'Aquasoft login — dealers can sign in with their Aquasoft email/password'
                  )}
                />
              )}
              {!isB2COnly && settings.allowAquasoftLogin !== false && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.allowBranchLogin !== false}
                      disabled={saving}
                      onChange={(e) => void handleBranchLoginToggle(e.target.checked)}
                    />
                  }
                  label={tt(
                    'Şube girişine izin ver — Aquasoft şube kullanıcıları da girebilsin (her şube kendi carisine kaydolur)',
                    'Allow branch login — Aquasoft branch users can sign in (each branch gets its own account)'
                  )}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Döviz Ayarları */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CurrencyExchangeIcon color="primary" />
          <Typography variant="h6">{tt('Döviz Ayarları', 'Currency Settings')}</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="store-currency-label">{tt('Mağaza Para Birimi', 'Store Currency')}</InputLabel>
              <Select
                labelId="store-currency-label"
                value={settings.storeCurrency || 'TRY'}
                label={tt('Mağaza Para Birimi', 'Store Currency')}
                onChange={(e) => handleChange('storeCurrency', e.target.value)}
              >
                {currencyOptions.map(code => (
                  <MenuItem key={code} value={code}>{code}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="rate-mode-label">{tt('Kur Modu', 'Rate Mode')}</InputLabel>
              <Select
                labelId="rate-mode-label"
                value={settings.exchangeRateMode || 'manual'}
                label={tt('Kur Modu', 'Rate Mode')}
                onChange={(e) => handleChange('exchangeRateMode', e.target.value)}
              >
                <MenuItem value="manual">{tt('Manuel — kuru elle gir', 'Manual — enter rate by hand')}</MenuItem>
                <MenuItem value="auto">{tt('Otomatik — günlük TCMB kuru', 'Auto — daily TCMB rate')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {settings.exchangeRateMode === 'manual' && (
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label={tt('Manuel Kur', 'Manual Rate')}
                value={settings.manualExchangeRate ?? ''}
                onChange={(e) => handleChange('manualExchangeRate', e.target.value === '' ? null : parseFloat(e.target.value))}
                InputProps={{
                  inputProps: { min: 0, step: 0.0001 },
                  endAdornment: <InputAdornment position="end">₺</InputAdornment>
                }}
                helperText={tt('1 birim döviz kaç TL', 'TL value of 1 unit of currency')}
              />
            </Grid>
          )}
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!settings.showExchangeRateInHeader}
                  onChange={(e) => handleChange('showExchangeRateInHeader', e.target.checked)}
                />
              }
              label={tt('Kuru vitrin header\'ında göster', 'Show rate in storefront header')}
            />
          </Grid>
          {settings.storeCurrency !== 'TRY' && (
            <>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!settings.b2cPricesInTry}
                      onChange={(e) => handleChange('b2cPricesInTry', e.target.checked)}
                    />
                  }
                  label={tt('Bireysel (B2C) müşteriye fiyatları TL göster', 'Show prices in TL to retail (B2C) customers')}
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  {settings.b2cPricesInTry
                    ? tt(
                        `Bireysel müşteriler ürün, sepet ve ödemede tutarları kur ile TL'ye çevrilmiş görür ve TL öder; bayiler (B2B) ${settings.storeCurrency} görmeye devam eder.`,
                        `Retail customers see product, cart and checkout amounts converted to TL and pay in TL; dealers (B2B) keep seeing ${settings.storeCurrency}.`
                      )
                    : tt(
                        `Kapalıyken herkes fiyatları ${settings.storeCurrency} görür; online ödemede tutar kur ile TL'ye çevrilerek tahsil edilir.`,
                        `When off, everyone sees prices in ${settings.storeCurrency}; online payments are charged in TL using the exchange rate.`
                      )}
                </Alert>
              </Grid>
            </>
          )}
          {settings.showExchangeRateInHeader && (
            <Grid item xs={12}>
              <Alert severity="info">
                {settings.exchangeRateMode === 'auto'
                  ? tt(
                      'Header\'daki kurlar (USD/EUR) TCMB\'den otomatik güncellenir.',
                      'Header rates (USD/EUR) are updated automatically from TCMB.'
                    )
                  : tt(
                      'Header\'daki kurlar Döviz tanımlarındaki USD/EUR kayıtlarından okunur.',
                      'Header rates are read from the USD/EUR records in currency definitions.'
                    )}
              </Alert>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Online Tahsilat Banka Hesabı */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AccountBalanceIcon color="primary" />
          <Typography variant="h6">{tt('Online Tahsilat Banka Hesabı', 'Online Collection Bank Account')}</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="collection-bank-label">{tt('Banka Hesabı', 'Bank Account')}</InputLabel>
              <Select
                labelId="collection-bank-label"
                value={settings.onlineCollectionBankAccountId != null ? String(settings.onlineCollectionBankAccountId) : ''}
                label={tt('Banka Hesabı', 'Bank Account')}
                onChange={(e) => handleChange('onlineCollectionBankAccountId', e.target.value === '' ? null : Number(e.target.value))}
              >
                <MenuItem value="">{tt('— Seçilmedi —', '— Not selected —')}</MenuItem>
                {bankAccounts.map(b => (
                  <MenuItem key={b.id} value={String(b.id)}>
                    {b.bankName || b.accountName || `#${b.id}`}{b.currency ? ` (${b.currency})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label={tt('Mağaza Adresi', 'Store Address')}
              placeholder="https://www.firma.com.tr"
              value={settings.storeUrl ?? ''}
              onChange={(e) => handleChange('storeUrl', e.target.value)}
              helperText={tt(
                'Cari ödeme linkleri bu adresle oluşturulur. Boşsa ödeme linki gönderilemez.',
                'Account payment links are created with this address. Leave empty to disable payment links.'
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Alert severity={settings.onlineCollectionBankAccountId ? 'info' : 'warning'}>
              {settings.onlineCollectionBankAccountId
                ? tt(
                    'Online sipariş tahsilatları ve cari bakiye yüklemeleri bu banka hesabına işlenir.',
                    'Online order collections and account balance top-ups are posted to this bank account.'
                  )
                : tt(
                    'Banka hesabı seçilmezse online tahsilatların muhasebeleştirilmesi (fatura + cari kayıt) reddedilir.',
                    'If no bank account is selected, posting of online collections (invoice + ledger entry) will be rejected.'
                  )}
            </Alert>
          </Grid>
        </Grid>
      </Paper>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSaveClick}
          disabled={saving}
        >
          {saving ? tt('Kaydediliyor...', 'Saving...') : tt('Kaydet', 'Save')}
        </Button>
      </Box>
      </>
      )}

      {/* Faz 5.6: hybrid → b2b_only mod değişimi uyarısı */}
      <Dialog open={modeWarningOpen} onClose={() => setModeWarningOpen(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          {tt('Mağaza modunu değiştirmek üzeresiniz', 'You are about to change the store mode')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {tt(
              'Mağaza "Sadece B2B" moduna alınacak. Bu durumda:',
              'The store will be switched to "B2B only" mode. As a result:'
            )}
            <ul style={{ marginTop: 8, marginBottom: 0 }}>
              <li>{tt('İsterseniz "Giriş zorunlu" ile ziyaretçileri login sayfasına yönlendirebilirsiniz.', 'Optionally turn on "Require login" to send visitors to the login page.')}</li>
              <li>{tt('Onaylı B2B üyesi olmayan mevcut B2C üyeler alışveriş yapamaz.', 'Existing B2C members without approved B2B status cannot shop.')}</li>
              <li>{tt('B2C kayıt ayarı kapalıysa yeni son kullanıcı kaydı alınmaz.', 'If B2C registration is disabled, new end-user sign-ups are blocked.')}</li>
            </ul>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModeWarningOpen(false)}>{tt('Vazgeç', 'Cancel')}</Button>
          <Button variant="contained" color="warning" onClick={doSave}>
            {tt('Evet, Sadece B2B yap', 'Yes, switch to B2B only')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default B2BManagement;
